import { ReactElement, useEffect, useMemo, useRef } from 'react'
import { useLatest } from 'react-use'
import { Vector2, Vector3 } from 'three'
import { useEmitMarqueeSelectionListener, useIsKeyPressed } from '@editor/hooks'
import { useControlStore } from '@editor/stores'
import { useCanvasListener } from '@modugen/ui-toolkit'
import { useThree } from '@react-three/fiber'
import { pointerModifiers } from 'src/shortcuts/pointerModifiers'
import {
  createMarqueeSelectionCache,
  marqueeObjectSelection,
  resetMarqueeSelectionCache,
} from './utils'

interface TransientSelectionState {
  startPoint: Vector2
  startPointInCanvas: Vector3
  pointTopLeft: Vector2
  pointBottomRight: Vector2
}

interface PendingSelectionState {
  frustumStart: Vector2
  frustumEnd: Vector2
  mode: 'full' | 'touch'
}

const MIN_SELECTION_SAMPLE_DISTANCE_PX = 96 / 2.54

const MarqueeSelectionController = (): ReactElement => {
  const gl = useThree(state => state.gl)
  const scene = useThree(state => state.scene)
  const camera = useThree(state => state.camera)

  const setIsMarqueeSelection = useControlStore(state => state.setIsMarqueeSelection)

  const transientSelectionState = useRef<TransientSelectionState>({
    startPoint: new Vector2(),
    startPointInCanvas: new Vector3(),
    pointTopLeft: new Vector2(),
    pointBottomRight: new Vector2(),
  })
  const pendingSelectionState = useRef<PendingSelectionState>({
    frustumStart: new Vector2(),
    frustumEnd: new Vector2(),
    mode: 'full',
  })
  const lastSelectionSamplePoint = useRef(new Vector2())
  const hasSelectionSamplePoint = useRef(false)
  const selectionFrameId = useRef<number | null>(null)

  // Broad-phase cache for the current drag. The scene is walked once per drag to
  // precompute selectable entries and their world AABBs; every subsequent sample
  // reuses it. It is rebuilt at drag start and cleared at drag end.
  const selectionCache = useRef(createMarqueeSelectionCache())

  // the selection rectangle div is used to display the rectangle drawn by the
  // user to do the marquee selection. the size of the rectangle is updated on
  // mouse move
  const selectionRectangleDiv = useMemo(() => {
    const div = document.createElement('div')
    div.style.border = '1px solid #55aaff'
    div.style.backgroundColor = 'rgba(75, 160, 255, 0.1)'
    div.style.position = 'absolute'
    div.style.pointerEvents = 'none'
    div.style.display = 'none'

    div.style.width = '0'
    div.style.height = '0'

    div.style.top = '0'
    div.style.left = '0'

    return div
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gl])

  const { isPressed: isMarqueeModifierDown, syncPressedState: syncMarqueeModifierState } =
    useIsKeyPressed(pointerModifiers.marquee)

  const hasEnabledListeners = useControlStore(state => {
    for (const listenerConfig of state.marqueeSelectionListeners.values()) {
      if (listenerConfig.enabled) return true
    }
    return false
  })
  const isActive = isMarqueeModifierDown && hasEnabledListeners

  useEffect(() => {
    gl.domElement.parentElement?.append(selectionRectangleDiv)
    return () => selectionRectangleDiv.remove()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gl])

  useEffect(() => {
    if (!isActive) {
      selectionRectangleDiv.style.display = 'none'
      selectionRectangleDiv.style.width = '0px'
      selectionRectangleDiv.style.height = '0px'
      hasSelectionSamplePoint.current = false
      resetMarqueeSelectionCache(selectionCache.current)
      if (selectionFrameId.current !== null) {
        cancelAnimationFrame(selectionFrameId.current)
        selectionFrameId.current = null
      }
    }
  }, [isActive, selectionRectangleDiv])

  const emitMarqueeSelection = useEmitMarqueeSelectionListener()
  const emitMarqueeSelectionRef = useLatest(emitMarqueeSelection)

  useEffect(() => {
    return () => {
      if (selectionFrameId.current !== null) {
        cancelAnimationFrame(selectionFrameId.current)
      }
    }
  }, [])

  useEffect(() => {
    setIsMarqueeSelection(isActive)
  }, [isActive, setIsMarqueeSelection])

  useCanvasListener(
    'mousemove',
    event => {
      const isModifierDown = syncMarqueeModifierState(event)
      const isActiveForEvent = hasEnabledListeners && isModifierDown

      if (!isActiveForEvent) return

      // set start point if not yet set
      if (selectionRectangleDiv.style.display === 'none') {
        selectionRectangleDiv.style.display = 'block'

        selectionRectangleDiv.style.left = event.offsetX + 'px'
        selectionRectangleDiv.style.top = event.offsetY + 'px'
        selectionRectangleDiv.style.width = '0px'
        selectionRectangleDiv.style.height = '0px'

        // start of a new drag - invalidate the broad-phase cache so it gets
        // rebuilt from the current scene state on the next selection sample
        resetMarqueeSelectionCache(selectionCache.current)

        transientSelectionState.current.startPoint.x = event.offsetX
        transientSelectionState.current.startPoint.y = event.offsetY

        transientSelectionState.current.startPointInCanvas = new Vector3(
          (event.offsetX / gl.domElement.clientWidth) * 2 - 1,
          -(event.offsetY / gl.domElement.clientHeight) * 2 + 1,
        )
        lastSelectionSamplePoint.current.set(event.offsetX, event.offsetY)
        hasSelectionSamplePoint.current = true
      }
      transientSelectionState.current.pointBottomRight.x = Math.max(
        transientSelectionState.current.startPoint.x,
        event.offsetX,
      )
      transientSelectionState.current.pointBottomRight.y = Math.max(
        transientSelectionState.current.startPoint.y,
        event.offsetY,
      )
      transientSelectionState.current.pointTopLeft.x = Math.min(
        transientSelectionState.current.startPoint.x,
        event.offsetX,
      )
      transientSelectionState.current.pointTopLeft.y = Math.min(
        transientSelectionState.current.startPoint.y,
        event.offsetY,
      )

      selectionRectangleDiv.style.left = transientSelectionState.current.pointTopLeft.x + 'px'
      selectionRectangleDiv.style.top = transientSelectionState.current.pointTopLeft.y + 'px'
      selectionRectangleDiv.style.width =
        transientSelectionState.current.pointBottomRight.x -
        transientSelectionState.current.pointTopLeft.x +
        'px'
      selectionRectangleDiv.style.height =
        transientSelectionState.current.pointBottomRight.y -
        transientSelectionState.current.pointTopLeft.y +
        'px'

      const endPointInCanvas = new Vector3(
        (event.offsetX / gl.domElement.clientWidth) * 2 - 1,
        -(event.offsetY / gl.domElement.clientHeight) * 2 + 1,
      )

      const frustumStart = new Vector2(
        transientSelectionState.current.startPointInCanvas.x,
        transientSelectionState.current.startPointInCanvas.y,
      )
      const frustumEnd = new Vector2(endPointInCanvas.x, endPointInCanvas.y)

      // when the selection is done from left to right, all elements that are
      // touching the rectangle should be selected, if it is done from right
      // to left all elements that are fully within the selection (all
      // vertices are within the rectangle) will be selected
      const mode: 'full' | 'touch' = frustumStart.x - frustumEnd.x > 0 ? 'touch' : 'full'

      // there is a really short moment where the whole model will be selected
      // at the beginning of the selection. This can be omitted using the
      // following check
      if (frustumStart.distanceTo(frustumEnd) > 0.01) {
        const deltaX = event.offsetX - lastSelectionSamplePoint.current.x
        const deltaY = event.offsetY - lastSelectionSamplePoint.current.y
        const distanceSquaredSinceLastSample = deltaX * deltaX + deltaY * deltaY

        if (
          hasSelectionSamplePoint.current &&
          distanceSquaredSinceLastSample <
            MIN_SELECTION_SAMPLE_DISTANCE_PX * MIN_SELECTION_SAMPLE_DISTANCE_PX
        ) {
          return
        }

        lastSelectionSamplePoint.current.set(event.offsetX, event.offsetY)
        hasSelectionSamplePoint.current = true
        pendingSelectionState.current.frustumStart.copy(frustumStart)
        pendingSelectionState.current.frustumEnd.copy(frustumEnd)
        pendingSelectionState.current.mode = mode

        if (selectionFrameId.current === null) {
          selectionFrameId.current = requestAnimationFrame(() => {
            selectionFrameId.current = null

            const { objectsInFrustum, idsInFrustum } = marqueeObjectSelection(
              pendingSelectionState.current.frustumStart,
              pendingSelectionState.current.frustumEnd,
              camera,
              scene,
              pendingSelectionState.current.mode,
              gl.clippingPlanes,
              selectionCache.current,
            )

            emitMarqueeSelectionRef.current(idsInFrustum, objectsInFrustum)
          })
        }
      }
    },
    [camera, hasEnabledListeners, syncMarqueeModifierState],
  )

  return <></>
}

export default MarqueeSelectionController
